/**
 * Admin Email Marketing console.
 *
 * Three-tab page:
 *   1. Compose — pick a built-in template (or scratch), edit subject/preheader/HTML,
 *      attach a coupon, pick the audience, preview, test-send, and blast.
 *   2. Campaigns — history with per-campaign stats and a drill-in detail view.
 *   3. Templates — list of saved + built-in templates, with a "use as base" action.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { formatDate, capitalize, formatPrice } from '../../utils/helpers';
import Icon from './Icon';
import Logo from '../Logo';

const STARTER_HTML = `<!-- Pick a template above, or write your own HTML / paste from your designer. -->
<!-- Available merge tags: {{firstName}} {{couponCode}} {{couponDescription}} {{shopUrl}} {{accountUrl}} {{unsubscribeUrl}} -->
<div style="font-family:sans-serif;padding:32px;color:#222;">
  <h1>Hi {{firstName}},</h1>
  <p>Replace this with your message.</p>
</div>`;

export default function MarketingPage() {
  const [tab, setTab] = useState('compose');

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Icon name="marketing" className="h-6 w-6 text-tea-600" /> Email Marketing</h2>
          <p className="text-sm text-gray-500 mt-1">Compose, preview, and blast emails to your users.</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-sm">
          {[
            ['compose', 'Compose'],
            ['campaigns', 'Campaigns'],
            ['templates', 'Templates'],
            ['automated', 'Automated'],
            ['social', 'Social proof'],
            ['brandkit', 'Brand Kit'],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={'px-4 py-1.5 rounded-md font-medium transition-colors ' +
                (tab === k ? 'bg-white shadow text-tea-700' : 'text-gray-500 hover:text-gray-700')}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'compose'   && <Compose />}
      {tab === 'campaigns' && <CampaignsList />}
      {tab === 'templates' && <TemplatesList />}
      {tab === 'automated' && <AutomatedFlow />}
      {tab === 'social'    && <SocialProof />}
      {tab === 'brandkit'  && <BrandKit />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   SOCIAL PROOF — storefront "recent orders" popup
// ═══════════════════════════════════════════════════════════════════════════

function SocialProof() {
  const [data, setData] = useState(null);    // { enabled, counts, events }
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(40);

  const load = () => {
    api.get('/admin/marketing/social-proof')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load social proof'));
  };
  useEffect(load, []);

  const toggle = async (enabled) => {
    // Optimistic flip so the switch feels instant.
    setData(d => (d ? { ...d, enabled } : d));
    try {
      await api.put('/admin/marketing/social-proof/settings', { enabled });
      toast.success(enabled ? 'Popup is now live on the storefront' : 'Popup turned off');
    } catch (err) {
      setData(d => (d ? { ...d, enabled: !enabled } : d));
      toast.error(err.response?.data?.error || 'Could not update setting');
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/admin/marketing/social-proof/generate', { count: Number(count) || 40 });
      toast.success(`Spun up ${res.created} placeholder order${res.created === 1 ? '' : 's'} — popup is live.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not generate orders');
    } finally {
      setBusy(false);
    }
  };

  const clearPlaceholders = async () => {
    if (!window.confirm('Remove all placeholder demo orders? Real orders are kept.')) return;
    setBusy(true);
    try {
      const { data: res } = await api.delete('/admin/marketing/social-proof/placeholders');
      toast.success(`Cleared ${res.removed} placeholder order${res.removed === 1 ? '' : 's'}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not clear placeholders');
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/admin/marketing/social-proof/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const counts = data?.counts || { total: 0, placeholders: 0, real: 0 };

  return (
    <div className="space-y-6">
      {/* Control card */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <Icon name="broadcast" className="h-5 w-5 text-tea-600" /> Recent-orders popup
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Shows a small “someone just ordered” card in the corner of the storefront as orders
              come in — classic social proof that nudges browsers to buy. Spin up placeholder
              orders below to see exactly how it looks while real volume builds.
            </p>
          </div>
          <ToggleSwitch on={!!data?.enabled} onChange={toggle} disabled={!data} />
        </div>

        {/* Counts */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Stat label="In feed" value={counts.total} />
          <Stat label="Placeholder" value={counts.placeholders} />
          <Stat label="Real orders" value={counts.real} />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">How many to generate</label>
            <input
              type="number" min={1} max={200}
              value={count}
              onChange={e => setCount(Math.max(1, Math.min(200, Number(e.target.value) || 0)))}
              className="w-28 text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <button onClick={generate} disabled={busy}
            className="text-sm font-bold px-4 py-2.5 rounded-lg bg-gradient-to-br from-tea-600 to-tea-700 text-white shadow hover:shadow-lg disabled:opacity-50">
            {busy ? 'Working…' : `Generate ${count} placeholder orders`}
          </button>
          {counts.placeholders > 0 && (
            <button onClick={clearPlaceholders} disabled={busy}
              className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">
              Clear placeholders
            </button>
          )}
        </div>
      </div>

      {/* Feed preview */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-700">
          What shoppers will see ({data?.events?.length || 0})
        </div>
        {data === null ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : (data.events || []).length === 0 ? (
          <div className="px-4 py-10 text-center text-gray-400 text-sm">
            No orders in the feed yet. Generate some placeholders above to preview the popup.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Shopper</th>
                <th className="text-left px-4 py-2 font-semibold">Location</th>
                <th className="text-left px-4 py-2 font-semibold">Product</th>
                <th className="text-left px-4 py-2 font-semibold">Total</th>
                <th className="text-left px-4 py-2 font-semibold">When</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.events.map(ev => (
                <tr key={ev.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-800">{ev.displayName}</td>
                  <td className="px-4 py-2 text-gray-500">{[ev.city, ev.region].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-2 text-gray-700">
                    {ev.productName}{ev.variantLabel ? <span className="text-gray-400"> · {ev.variantLabel}</span> : null}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-gray-600">{ev.amountInCents != null ? formatPrice(ev.amountInCents) : '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{formatDate(ev.occurredAt)}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => removeOne(ev.id)}
                      className="text-xs text-red-500 hover:text-red-700 font-medium">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ToggleSwitch({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={'inline-flex items-center gap-2 disabled:opacity-50'}
    >
      <span className={'relative inline-flex h-6 w-11 items-center rounded-full transition-colors ' +
        (on ? 'bg-tea-600' : 'bg-gray-300')}>
        <span className={'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
          (on ? 'translate-x-5' : 'translate-x-0.5')} />
      </span>
      <span className={'text-sm font-semibold ' + (on ? 'text-tea-700' : 'text-gray-500')}>
        {on ? 'Live' : 'Off'}
      </span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   COMPOSE
// ═══════════════════════════════════════════════════════════════════════════

// ─── Automated behavioral flow ───────────────────────────────────────────────
const FLOW_STEPS = [
  { key: 'cart_abandon', name: 'Abandoned cart', when: '4–72h after adding to cart, no order since',
    desc: 'Reminds them what they left behind, personalized with the carted item and related products they viewed.' },
  { key: 'reengage_7d', name: '7-day re-engagement', when: '~7 days after their last visit',
    desc: 'Brings back quiet browsers with the products they were looking at.' },
  { key: 'reengage_30d', name: '30-day re-engagement', when: '~30 days after their last visit',
    desc: 'A final nudge featuring their picks plus a VIP savings pitch.' },
];

function AutomatedFlow() {
  const [recent, setRecent] = useState(null);
  const [byStep, setByStep] = useState({});
  const [running, setRunning] = useState(false);

  const load = () => {
    api.get('/admin/marketing/flow/recent', { params: { limit: 50 } })
      .then(({ data }) => { setRecent(data.touches || []); setByStep(data.byStep || {}); })
      .catch(() => toast.error('Failed to load automated sends'));
  };
  useEffect(load, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/marketing/flow/run');
      const total = (data.cartAbandon || 0) + (data.reengage7d || 0) + (data.reengage30d || 0);
      toast.success(`Flow ran — ${total} email${total === 1 ? '' : 's'} sent (cart ${data.cartAbandon}, 7d ${data.reengage7d}, 30d ${data.reengage30d}).`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to run flow');
    } finally {
      setRunning(false);
    }
  };

  const stepName = (k) => FLOW_STEPS.find(s => s.key === k)?.name || k;

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <Icon name="marketing" className="h-5 w-5 text-tea-600" /> Behavioral email flow
            </h3>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Runs automatically every hour. Emails are personalized on what each user was looking at,
              respect marketing opt-outs, and never repeat the same step inside its cooldown.
            </p>
          </div>
          <button onClick={runNow} disabled={running}
            className="px-4 py-2 rounded-lg bg-tea-600 text-white text-sm font-medium hover:bg-tea-700 disabled:opacity-50">
            {running ? 'Running…' : 'Run now'}
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mt-4">
          {FLOW_STEPS.map(s => (
            <div key={s.key} className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-gray-800">{s.name}</span>
                <span className="text-xs font-bold text-tea-700 bg-tea-50 rounded-full px-2 py-0.5">
                  {byStep[s.key] || 0} sent
                </span>
              </div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mt-1.5">{s.when}</div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-700">Recent automated sends</div>
        {recent === null ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">No automated emails sent yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                <th className="text-left px-4 py-2 font-semibold">Step</th>
                <th className="text-left px-4 py-2 font-semibold">Sent</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(t => (
                <tr key={t.id} className="border-b last:border-0">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{t.user ? `${t.user.firstName} ${t.user.lastName}` : '—'}</div>
                    <div className="text-xs text-gray-400">{t.user?.email}</div>
                  </td>
                  <td className="px-4 py-2 text-gray-600">{stepName(t.triggerKey)}</td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(t.sentAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Compose() {
  const [templates, setTemplates] = useState([]);
  const [coupons, setCoupons]     = useState([]);
  const [seeding, setSeeding]     = useState(false);
  const [campaign, setCampaign]   = useState({
    name: '', subject: '', preheader: '', htmlBody: STARTER_HTML,
    templateId: '', couponId: '',
    audienceFilter: { roles: [], onlyVerified: false, onlySubscribers: false, neverPurchased: false },
  });
  const [audienceCount, setAudienceCount] = useState(null);
  const [previewOpen, setPreviewOpen]     = useState(false);
  const [testEmail, setTestEmail]         = useState('');
  const [sendingTest, setSendingTest]     = useState(false);
  const [blasting, setBlasting]           = useState(false);

  // Load templates + coupons + auto-seed built-ins on first visit if empty.
  // Templates and coupons load independently so a failure in one (e.g. a backend
  // that hasn't had its migrations run) doesn't blank the whole page — and we
  // surface the real backend error instead of a generic "failed" toast.
  useEffect(() => {
    const load = async () => {
      try {
        const tRes = await api.get('/admin/marketing/templates');
        let tpls = tRes.data.templates || [];
        if (!tpls.some(t => t.isBuiltin)) {
          setSeeding(true);
          try {
            const seed = await api.post('/admin/marketing/templates/seed');
            tpls = seed.data.templates || tpls;
          } finally {
            setSeeding(false);
          }
        }
        setTemplates(tpls);
      } catch (err) {
        const detail = err.response?.data?.error || err.message;
        toast.error(`Couldn't load email templates: ${detail}`);
      }

      try {
        const cRes = await api.get('/admin/coupons', { params: { status: 'active', limit: 100 } });
        setCoupons(cRes.data.data || []);
      } catch (err) {
        // Coupons are optional when composing a campaign — degrade gracefully.
        const detail = err.response?.data?.error || err.message;
        toast.error(`Couldn't load coupons: ${detail}`);
      }
    };
    load();
  }, []);

  // Recompute audience count when filter changes (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/admin/marketing/audience-count', campaign.audienceFilter);
        setAudienceCount(data.count);
      } catch { setAudienceCount(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [campaign.audienceFilter]);

  const applyTemplate = (templateId) => {
    if (!templateId) {
      setCampaign(c => ({ ...c, templateId: '' }));
      return;
    }
    const t = templates.find(t => t.id === templateId);
    if (!t) return;
    setCampaign(c => ({
      ...c,
      templateId,
      subject: c.subject || t.subject || '',
      preheader: c.preheader || t.preheader || '',
      htmlBody: t.htmlBody,
      name: c.name || t.name,
    }));
  };

  const toggleArrayValue = (key, value) => {
    setCampaign(c => {
      const cur = c.audienceFilter[key] || [];
      const next = cur.includes(value) ? cur.filter(x => x !== value) : [...cur, value];
      return { ...c, audienceFilter: { ...c.audienceFilter, [key]: next } };
    });
  };

  const setFlag = (key, value) =>
    setCampaign(c => ({ ...c, audienceFilter: { ...c.audienceFilter, [key]: value } }));

  // Build a campaign draft so we can hit test/send endpoints
  const ensureDraft = async () => {
    if (campaign.id) return campaign.id;
    if (!campaign.name.trim()) {
      toast.error('Give your campaign a name first.');
      return null;
    }
    if (!campaign.subject.trim()) {
      toast.error('Subject line is required.');
      return null;
    }
    const { data } = await api.post('/admin/marketing/campaigns', {
      name: campaign.name.trim(),
      subject: campaign.subject.trim(),
      preheader: campaign.preheader || null,
      htmlBody: campaign.htmlBody,
      templateId: campaign.templateId || null,
      couponId: campaign.couponId || null,
      audienceFilter: campaign.audienceFilter,
    });
    setCampaign(c => ({ ...c, id: data.id }));
    return data.id;
  };

  const saveDraft = async () => {
    try {
      const id = await ensureDraft();
      if (!id) return;
      // ensureDraft creates a new one if none, else update existing
      if (campaign.id === id) {
        await api.put(`/admin/marketing/campaigns/${id}`, {
          name: campaign.name,
          subject: campaign.subject,
          preheader: campaign.preheader || null,
          htmlBody: campaign.htmlBody,
          templateId: campaign.templateId || null,
          couponId: campaign.couponId || null,
          audienceFilter: campaign.audienceFilter,
        });
      }
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error('Enter a test email'); return; }
    setSendingTest(true);
    try {
      const id = await ensureDraft();
      if (!id) return;
      await api.post(`/admin/marketing/campaigns/${id}/test`, { to: testEmail.trim() });
      toast.success(`Test sent to ${testEmail.trim()}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const blast = async () => {
    if (!audienceCount) {
      toast.error('Your audience filter matches 0 people. Adjust filters first.');
      return;
    }
    if (!window.confirm(
      `Send this email to ${audienceCount} ${audienceCount === 1 ? 'person' : 'people'}? This cannot be undone.`
    )) return;
    setBlasting(true);
    try {
      const id = await ensureDraft();
      if (!id) return;
      // Save latest edits before blasting
      await api.put(`/admin/marketing/campaigns/${id}`, {
        name: campaign.name,
        subject: campaign.subject,
        preheader: campaign.preheader || null,
        htmlBody: campaign.htmlBody,
        templateId: campaign.templateId || null,
        couponId: campaign.couponId || null,
        audienceFilter: campaign.audienceFilter,
      });
      await api.post(`/admin/marketing/campaigns/${id}/send`);
      toast.success('Campaign is sending in the background. Check the Campaigns tab for progress.');
      // Reset compose form
      setCampaign({
        name: '', subject: '', preheader: '', htmlBody: STARTER_HTML,
        templateId: '', couponId: '',
        audienceFilter: { roles: [], onlyVerified: false, onlySubscribers: false, neverPurchased: false },
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Send failed');
    } finally {
      setBlasting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

      {/* Left: composer */}
      <div className="xl:col-span-2 space-y-4">

        {/* Template gallery */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-gray-700 flex items-center gap-2"><Icon name="file" className="h-4 w-4 text-tea-600" /> Start from a template</h3>
            {seeding && <span className="text-xs text-tea-600">Installing built-in templates…</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {templates.map(t => (
              <button key={t.id} onClick={() => applyTemplate(t.id)}
                className={'text-left border rounded-lg p-3 transition-all hover:border-tea-400 hover:shadow-sm ' +
                  (campaign.templateId === t.id ? 'border-tea-600 bg-tea-50 ring-1 ring-tea-200' : 'border-gray-200 bg-white')}>
                <div className="text-[10px] uppercase tracking-wider text-tea-600 font-bold">{t.category || 'custom'}</div>
                <div className="font-semibold text-sm text-gray-800 mt-0.5">{t.name}</div>
                <div className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</div>
              </button>
            ))}
            <button onClick={() => setCampaign(c => ({ ...c, templateId: '', htmlBody: STARTER_HTML }))}
              className="text-left border-2 border-dashed rounded-lg p-3 hover:border-tea-400">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">From scratch</div>
              <div className="font-semibold text-sm text-gray-800 mt-0.5">Blank canvas</div>
              <div className="text-xs text-gray-500 mt-1">Paste your own HTML or text.</div>
            </button>
          </div>
        </div>

        {/* Subject / preheader / name */}
        <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3">
          <Field label="Campaign name (internal)" hint="Only you see this — for your records.">
            <input value={campaign.name} onChange={e => setCampaign(c => ({ ...c, name: e.target.value }))}
              placeholder="e.g. June flash sale"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>
          <Field label="Subject line">
            <input value={campaign.subject} onChange={e => setCampaign(c => ({ ...c, subject: e.target.value }))}
              placeholder="What lands in their inbox"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>
          <Field label="Preheader (preview text)" hint="The little gray line beside the subject in most inboxes.">
            <input value={campaign.preheader} onChange={e => setCampaign(c => ({ ...c, preheader: e.target.value }))}
              placeholder="A short hook — 60 to 100 characters"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>
        </div>

        {/* HTML editor */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="font-bold text-sm text-gray-700 flex items-center gap-2"><Icon name="file" className="h-4 w-4 text-tea-600" /> Email body (HTML or text)</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setPreviewOpen(true)}
                className="text-xs font-medium px-3 py-1 rounded-md border border-tea-300 text-tea-700 hover:bg-tea-50">
                Preview
              </button>
            </div>
          </div>
          <textarea
            value={campaign.htmlBody}
            onChange={e => setCampaign(c => ({ ...c, htmlBody: e.target.value }))}
            rows={20}
            spellCheck={false}
            className="w-full font-mono text-xs border border-gray-300 rounded-lg p-3 leading-relaxed text-gray-800 focus:outline-none focus:border-tea-400"
          />
          <p className="text-[11px] text-gray-400 mt-2">
            Merge tags:&nbsp;
            <code>{`{{firstName}}`}</code>, <code>{`{{email}}`}</code>,
            <code>{` {{couponCode}}`}</code>, <code>{` {{couponDescription}}`}</code>,
            <code>{` {{shopUrl}}`}</code>, <code>{` {{accountUrl}}`}</code>,
            <code>{` {{unsubscribeUrl}}`}</code>
          </p>
        </div>
      </div>

      {/* Right: audience + coupon + actions */}
      <div className="space-y-4">

        {/* Audience */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <h3 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2"><Icon name="pin" className="h-4 w-4 text-tea-600" /> Audience</h3>

          <Group label="Role">
            {['customer', 'admin'].map(r => (
              <Chip key={r} active={(campaign.audienceFilter.roles || []).includes(r)}
                onClick={() => toggleArrayValue('roles', r)}>{capitalize(r)}</Chip>
            ))}
          </Group>

          <div className="space-y-2 mt-3 text-sm">
            <CheckRow label="Only verified emails"
              checked={!!campaign.audienceFilter.onlyVerified}
              onChange={v => setFlag('onlyVerified', v)} />
            <CheckRow label="Only active subscribers"
              checked={!!campaign.audienceFilter.onlySubscribers}
              onChange={v => setFlag('onlySubscribers', v)} />
            <CheckRow label="Only past purchasers"
              checked={!!campaign.audienceFilter.onlyEverPurchased}
              onChange={v => setFlag('onlyEverPurchased', v)} />
            <CheckRow label="Never purchased"
              checked={!!campaign.audienceFilter.neverPurchased}
              onChange={v => setFlag('neverPurchased', v)} />
            <CheckRow label="Added something to cart"
              checked={!!campaign.audienceFilter.addedToCart}
              onChange={v => setFlag('addedToCart', v)} />
          </div>

          {/* Behavioral targeting — based on what they were looking at */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">
              Looked at a product
            </div>
            <input
              type="text"
              value={campaign.audienceFilter.viewedProductSlug || ''}
              onChange={e => setFlag('viewedProductSlug', e.target.value.trim())}
              placeholder="product-slug (e.g. bpc-157)"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white" />
            {(campaign.audienceFilter.viewedProductSlug || campaign.audienceFilter.addedToCart) && (
              <label className="flex items-center justify-between gap-2 mt-2 text-xs text-gray-500">
                <span>Within the last</span>
                <span className="flex items-center gap-1">
                  <input
                    type="number" min={1} max={365}
                    value={campaign.audienceFilter.behaviorDays || 30}
                    onChange={e => setFlag('behaviorDays', Math.max(1, Number(e.target.value) || 30))}
                    className="w-16 text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white text-right" />
                  <span>days</span>
                </span>
              </label>
            )}
          </div>

          <div className="mt-4 bg-tea-50 border border-tea-200 rounded-lg p-3 text-center">
            <div className="text-xs text-tea-700 font-semibold uppercase tracking-wide">Will reach</div>
            <div className="text-3xl font-bold text-tea-700 mt-1">
              {audienceCount === null ? '…' : audienceCount.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">people opted-in to marketing</div>
          </div>
        </div>

        {/* Coupon picker */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <h3 className="font-bold text-sm text-gray-700 mb-2 flex items-center gap-2"><Icon name="ticket" className="h-4 w-4 text-tea-600" /> Attach a coupon (optional)</h3>
          <select
            value={campaign.couponId}
            onChange={e => setCampaign(c => ({ ...c, couponId: e.target.value }))}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
            <option value="">— No coupon —</option>
            {coupons.map(c => (
              <option key={c.id} value={c.id}>
                {c.code} · {couponLabel(c)}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-gray-400 mt-2">
            Templates with <code>{`{{couponCode}}`}</code> and <code>{`{{couponDescription}}`}</code> will auto-fill with this code.
          </p>
        </div>

        {/* Actions */}
        <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3">
          <div>
            <h3 className="font-bold text-sm text-gray-700 mb-2 flex items-center gap-2"><Icon name="lab" className="h-4 w-4 text-tea-600" /> Send yourself a test</h3>
            <div className="flex gap-2">
              <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2" />
              <button onClick={sendTest} disabled={sendingTest}
                className="text-sm font-semibold px-4 py-2 rounded-lg border border-tea-300 text-tea-700 hover:bg-tea-50 disabled:opacity-50">
                {sendingTest ? 'Sending…' : 'Send test'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            <button onClick={saveDraft}
              className="text-sm font-semibold px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              Save draft
            </button>
            <button onClick={blast} disabled={blasting || !audienceCount}
              className="text-sm font-bold px-4 py-2.5 rounded-lg bg-gradient-to-br from-tea-600 to-tea-700 text-white shadow hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
              {blasting ? 'Sending…' : `Blast to ${audienceCount || 0}`}
            </button>
          </div>
        </div>

      </div>

      {previewOpen && (
        <PreviewModal
          html={campaign.htmlBody}
          subject={campaign.subject}
          coupon={coupons.find(c => c.id === campaign.couponId) || null}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={'text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ' +
        (active ? 'bg-tea-600 text-white border-tea-600' : 'bg-white text-gray-600 border-gray-300 hover:border-tea-400')}>
      {children}
    </button>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-tea-600 focus:ring-tea-500" />
      {label}
    </label>
  );
}

function couponLabel(c) {
  if (c.discountType === 'percent') return `${c.discountValue}% off`;
  if (c.discountType === 'fixed_amount') return `$${(c.discountValue / 100).toFixed(2)} off`;
  if (c.discountType === 'free_shipping') return 'Free shipping';
  return c.discountType;
}

// ─── Preview modal ──────────────────────────────────────────────────────────
function PreviewModal({ html, subject, coupon, onClose }) {
  // Substitute merge tags client-side for the preview iframe
  const previewHtml = useMemo(() => {
    const couponDescription = coupon
      ? coupon.discountType === 'percent' ? `${coupon.discountValue}% off your order`
      : coupon.discountType === 'fixed_amount' ? `$${(coupon.discountValue / 100).toFixed(2)} off your order`
      : 'Free shipping on your next order'
      : '20% off your order';
    const vars = {
      firstName: 'Alex',
      email: 'preview@alohaamino.com',
      couponCode: coupon?.code || 'PREVIEW20',
      couponDescription,
      shopUrl: '/shop',
      accountUrl: '/account',
      unsubscribeUrl: '#',
      year: new Date().getFullYear(),
    };
    return String(html || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => vars[k] ?? '');
  }, [html, coupon]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
          <div className="text-sm">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Subject</div>
            <div className="font-bold text-gray-800">{subject || '(no subject)'}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe title="Email preview" srcDoc={previewHtml}
            sandbox="allow-same-origin"
            className="w-full h-full border-0" style={{ minHeight: '500px' }} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   CAMPAIGNS LIST
// ═══════════════════════════════════════════════════════════════════════════

function CampaignsList() {
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage]           = useState(1);
  const [pagination, setPagination] = useState({});
  const [detailId, setDetailId]   = useState(null);

  const load = () => {
    api.get('/admin/marketing/campaigns', { params: { page } })
      .then(res => { setCampaigns(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load campaigns'));
  };
  useEffect(load, [page]);

  // Poll while any campaign is sending
  useEffect(() => {
    const anySending = campaigns.some(c => c.status === 'sending');
    if (!anySending) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm('Delete this campaign? Only drafts can be removed.')) return;
    try {
      await api.delete(`/admin/marketing/campaigns/${id}`);
      load();
      toast.success('Campaign deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Name', 'Subject', 'Status', 'Reached', 'Opens', 'Clicks', 'Sent', ''].map(h => (
                <th key={h} className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-gray-400">No campaigns yet. Head to Compose to send your first one.</td></tr>
            )}
            {campaigns.map(c => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setDetailId(c.id)}>
                <td className="px-3 py-2.5 font-medium text-gray-900">{c.name}</td>
                <td className="px-3 py-2.5 text-gray-600 truncate max-w-[16rem]">{c.subject}</td>
                <td className="px-3 py-2.5">
                  <CampaignStatusBadge status={c.status} />
                </td>
                <td className="px-3 py-2.5 tabular-nums">{c.totalSent} / {c.totalTargeted}</td>
                <td className="px-3 py-2.5 tabular-nums">{c.totalOpened}</td>
                <td className="px-3 py-2.5 tabular-nums">{c.totalClicked}</td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{c.sentAt ? formatDate(c.sentAt) : '—'}</td>
                <td className="px-3 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                  {['draft', 'cancelled', 'failed'].includes(c.status) && (
                    <button onClick={() => remove(c.id)}
                      className="text-xs text-red-500 hover:text-red-700 font-medium">Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex gap-1 mt-3 justify-end">
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={'px-3 py-1 text-xs border rounded ' +
                (page === i + 1 ? 'bg-tea-600 text-white border-tea-600' : 'hover:border-tea-400')}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {detailId && <CampaignDetail id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function CampaignStatusBadge({ status }) {
  const map = {
    draft:     'bg-gray-100 text-gray-700',
    queued:    'bg-blue-100 text-blue-700',
    sending:   'bg-amber-100 text-amber-700 animate-pulse',
    sent:      'bg-blue-100 text-blue-700',
    failed:    'bg-red-100 text-red-700',
    cancelled: 'bg-gray-200 text-gray-500',
  };
  return (
    <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' + (map[status] || 'bg-gray-100')}>
      {capitalize(status)}
    </span>
  );
}

function CampaignDetail({ id, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get(`/admin/marketing/campaigns/${id}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => toast.error('Failed to load campaign'));
    load();
    const t = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl h-full shadow-2xl flex flex-col">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{data?.campaign.name || 'Campaign'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{data?.campaign.subject}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-6 py-4 border-b bg-gray-50">
              <Stat label="Targeted" value={data.campaign.totalTargeted} />
              <Stat label="Sent" value={data.campaign.totalSent} />
              <Stat label="Failed" value={data.campaign.totalFailed} highlight={data.campaign.totalFailed > 0} />
              <Stat label="Opens" value={data.campaign.totalOpened} />
              <Stat label="Clicks" value={data.campaign.totalClicked} />
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="text-xs text-gray-500 uppercase font-bold mb-2 tracking-wider">Recipients ({data.recipients.length})</div>
              <table className="w-full text-sm">
                <thead className="border-b text-xs text-gray-500">
                  <tr>
                    <th className="text-left py-2">Email</th>
                    <th className="text-left">Status</th>
                    <th className="text-right">Opens</th>
                    <th className="text-right">Clicks</th>
                    <th className="text-left">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recipients.map(r => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2">
                        <div className="truncate max-w-[18rem]">{r.email}</div>
                        {r.error && <div className="text-xs text-red-500 truncate max-w-[18rem]" title={r.error}>{r.error}</div>}
                      </td>
                      <td>
                        <span className={
                          'text-xs font-bold px-1.5 py-0.5 rounded ' +
                          ({ sent: 'bg-blue-50 text-blue-700',
                             opened: 'bg-blue-50 text-blue-700',
                             clicked: 'bg-purple-50 text-purple-700',
                             failed: 'bg-red-50 text-red-700',
                             pending: 'bg-gray-100 text-gray-500',
                             unsubscribed: 'bg-amber-50 text-amber-700' }[r.status] || 'bg-gray-100')
                        }>{r.status}</span>
                      </td>
                      <td className="text-right tabular-nums">{r.openCount}</td>
                      <td className="text-right tabular-nums">{r.clickCount}</td>
                      <td className="text-xs text-gray-500">{r.sentAt ? formatDate(r.sentAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={'rounded p-2 text-center ' + (highlight ? 'bg-red-50 border border-red-200' : 'bg-white border')}>
      <div className={'text-lg font-bold ' + (highlight ? 'text-red-600' : 'text-gray-800')}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   TEMPLATES LIST
// ═══════════════════════════════════════════════════════════════════════════

function TemplatesList() {
  const [templates, setTemplates] = useState([]);
  const [previewId, setPreviewId] = useState(null);

  const load = () => api.get('/admin/marketing/templates')
    .then(res => setTemplates(res.data.templates))
    .catch(() => toast.error('Failed to load templates'));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/admin/marketing/templates/${id}`);
      load();
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const seedBuiltins = async () => {
    try {
      const { data } = await api.post('/admin/marketing/templates/seed');
      setTemplates(data.templates);
      const msg = [];
      if (data.created > 0) msg.push(`Installed ${data.created}`);
      if (data.updated > 0) msg.push(`Refreshed ${data.updated}`);
      toast.success(msg.length ? msg.join(' · ') + ' built-in templates' : 'All built-in templates are up to date');
    } catch (err) {
      toast.error('Seed failed');
    }
  };

  const preview = templates.find(t => t.id === previewId);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Reusable email blueprints. Built-ins are read-only; duplicate any to customize.</p>
        <button onClick={seedBuiltins}
          className="text-xs font-semibold px-3 py-1.5 rounded-md border border-tea-300 text-tea-700 hover:bg-tea-50">
          Re-install built-ins
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(t => (
          <div key={t.id} className={'border rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow ' + (t.isBuiltin ? 'border-tea-200' : 'border-gray-200')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold text-tea-600">{t.category || 'custom'}</span>
              {t.isBuiltin && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600">★ Built-in</span>}
            </div>
            <h3 className="font-bold text-gray-800 mb-1">{t.name}</h3>
            <p className="text-xs text-gray-500 line-clamp-3 mb-3">{t.description}</p>
            <div className="text-xs text-gray-600 mb-3 truncate"><strong>Subject:</strong> {t.subject}</div>
            <div className="flex gap-2 text-xs">
              <button onClick={() => setPreviewId(t.id)}
                className="font-semibold px-3 py-1 rounded border border-tea-300 text-tea-700 hover:bg-tea-50">
                Preview
              </button>
              {!t.isBuiltin && (
                <button onClick={() => remove(t.id)}
                  className="font-semibold px-3 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <PreviewModal html={preview.htmlBody} subject={preview.subject} coupon={null}
          onClose={() => setPreviewId(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   BRAND KIT — logo previews, downloadable assets, brand reference
// ═══════════════════════════════════════════════════════════════════════════

const BRAND_ASSETS = [
  { key: 'wordmark',       label: 'Wordmark — charcoal', file: '/brand/aloha-amino-wordmark.svg',       png: true,  dark: false },
  { key: 'wordmark-white', label: 'Wordmark — white',    file: '/brand/aloha-amino-wordmark-white.svg', png: true,  dark: true  },
  { key: 'stacked',        label: 'Stacked lockup',      file: '/brand/aloha-amino-stacked.svg',        png: false, dark: false },
  { key: 'favicon',        label: 'Favicon tile',        file: '/favicon.svg',                          png: false, dark: false },
];

// Fredoka doesn't load inside an <img>-rendered SVG, so for PNG export we
// inline the font into the SVG as a base64 data URI first. Fetched once,
// cached for the session; on failure the PNG falls back to the system stack.
let fredokaFontPromise = null;
function getFredokaDataUri() {
  if (!fredokaFontPromise) {
    fredokaFontPromise = (async () => {
      const css = await (await fetch('https://fonts.googleapis.com/css2?family=Fredoka:wght@600&display=swap')).text();
      const blocks = css.split('@font-face').filter(b => b.includes('url('));
      const latin = blocks.find(b => /U\+0000-00FF/.test(b)) || blocks[blocks.length - 1] || '';
      const url = (latin.match(/url\((https:[^)]+)\)/) || [])[1];
      if (!url) return null;
      const buf = await (await fetch(url)).arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < bytes.length; i += 0x8000) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      }
      return `data:font/woff2;base64,${btoa(bin)}`;
    })().catch(() => null);
  }
  return fredokaFontPromise;
}

// Rasterize an SVG asset to a transparent PNG at `scale`x its viewBox size.
async function rasterizeSvgToPng(svgUrl, filename, scale = 3) {
  let svgText = await (await fetch(svgUrl)).text();

  // Dimensions from the viewBox (all brand SVGs are viewBox-only).
  const vb = ((svgText.match(/viewBox="([^"]+)"/) || [])[1] || '').split(/[\s,]+/).map(Number);
  const w = vb.length === 4 && vb[2] ? vb[2] : 600;
  const h = vb.length === 4 && vb[3] ? vb[3] : 160;

  // Embed Fredoka + explicit pixel size so the <img> raster matches the brand.
  const fontUri = await getFredokaDataUri();
  const style = fontUri
    ? `<style>@font-face{font-family:'Fredoka';font-style:normal;font-weight:600;src:url(${fontUri}) format('woff2');}</style>`
    : '';
  svgText = svgText.replace(/<svg([^>]*)>/, `<svg$1 width="${w}" height="${h}">${style}`);

  const svgBlobUrl = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('SVG failed to load'));
      i.src = svgBlobUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    const png = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new Error('PNG encoding failed');
    const dl = URL.createObjectURL(png);
    const a = document.createElement('a');
    a.href = dl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(dl), 1000);
  } finally {
    URL.revokeObjectURL(svgBlobUrl);
  }
}

function BrandKit() {
  const [pngBusy, setPngBusy] = useState(null); // asset key currently exporting

  const downloadPng = async (asset) => {
    setPngBusy(asset.key);
    try {
      const base = asset.file.split('/').pop().replace(/\.svg$/, '');
      await rasterizeSvgToPng(asset.file, `${base}@3x.png`);
    } catch (err) {
      toast.error('PNG export failed — download the SVG instead.');
    } finally {
      setPngBusy(null);
    }
  };

  const copyHex = (hex) => {
    navigator.clipboard?.writeText(hex)
      .then(() => toast.success(`${hex} copied`))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <div className="space-y-6">

      {/* Logo previews */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <Icon name="star" className="h-5 w-5 text-tea-600" /> Logo
        </h3>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          The brand mark is the lowercase “aloha amino” wordmark in Fredoka — charcoal on light
          surfaces, white on dark or colored surfaces. Use the assets below as-is; don’t re-set
          the type or recolor it.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center min-h-[7rem]">
            <Logo className="text-3xl" />
          </div>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center min-h-[7rem]">
            <Logo stacked className="text-3xl" />
          </div>
          <div className="rounded-lg p-4 flex items-center justify-center min-h-[7rem] bg-[#333333]">
            <Logo className="!text-white text-3xl" />
          </div>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-center min-h-[7rem]">
            <img src="/favicon.svg" alt="Aloha Amino favicon" className="h-14 w-14" />
          </div>
        </div>
      </div>

      {/* Downloads */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-700">Downloads</div>
        <div className="divide-y">
          {BRAND_ASSETS.map(asset => (
            <div key={asset.key} className="flex items-center justify-between gap-4 px-4 py-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className={'h-10 w-24 rounded-md border flex items-center justify-center shrink-0 ' +
                  (asset.dark ? 'bg-[#333333] border-[#333333]' : 'bg-white border-gray-200')}>
                  <img src={asset.file} alt="" className="max-h-7 max-w-[5rem]" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-gray-800">{asset.label}</div>
                  <div className="text-xs text-gray-400 truncate">{asset.file}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <a href={asset.file} download
                  className="text-xs font-semibold px-3 py-1.5 rounded-md border border-tea-300 text-tea-700 hover:bg-tea-50">
                  Download SVG
                </a>
                {asset.png && (
                  <button onClick={() => downloadPng(asset)} disabled={pngBusy !== null}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {pngBusy === asset.key ? 'Exporting…' : 'Download PNG (3×)'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2.5 bg-gray-50 border-t text-[11px] text-gray-400">
          PNGs export at 3× with a transparent background — right for decks, invoices, and partner sites.
        </div>
      </div>

      {/* Brand reference */}
      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <Icon name="file" className="h-5 w-5 text-tea-600" /> Brand reference
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <button onClick={() => copyHex('#333333')}
            className="text-left border border-gray-200 rounded-lg p-3 hover:border-tea-400 transition-colors">
            <div className="h-10 rounded-md bg-[#333333] mb-2" />
            <div className="font-semibold text-sm text-gray-800">Charcoal</div>
            <div className="text-xs text-gray-500 font-mono">#333333 · click to copy</div>
          </button>
          <button onClick={() => copyHex('#0A84FF')}
            className="text-left border border-gray-200 rounded-lg p-3 hover:border-tea-400 transition-colors">
            <div className="h-10 rounded-md bg-[#0A84FF] mb-2" />
            <div className="font-semibold text-sm text-gray-800">Brand Blue</div>
            <div className="text-xs text-gray-500 font-mono">#0A84FF · click to copy</div>
          </button>
          <div className="border border-gray-200 rounded-lg p-3">
            <div className="h-10 rounded-md bg-gray-50 mb-2 flex items-center justify-center text-xl font-semibold text-[#333333]"
              style={{ fontFamily: "'Fredoka', 'Baloo 2', ui-rounded, sans-serif" }}>
              Aa
            </div>
            <div className="font-semibold text-sm text-gray-800">Typeface</div>
            <div className="text-xs text-gray-500">Fredoka — logo only; Inter for body copy</div>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-3">
          Charcoal is the logo color; blue stays the action/accent color. The old blue-dot mark is retired.
        </p>
      </div>

    </div>
  );
}
